import type { ActivatedRouteSnapshot } from '@angular/router'
import type { ROUTE } from './links'

export type ScreenKey =
  | 'schedule' | 'wor'
  | 'paper-dsr' | 'paper-prf' | 'paper-forms'
  | 'tools'
  | 'timesheet' | 'expenses' | 'pto'
  | 'howto' | 'support' | 'about'

export type TipKey = ScreenKey | 'job-jsa'

export interface ScreenDef {
  /** The Back label of whatever is opened from this screen. The path comes from ROUTE via SCREEN_ROUTE. */
  readonly title: string
}

export const SCREENS = {
  'schedule': { title: 'Schedule' },
  'wor': { title: 'Work Order Requests' },
  'paper-dsr': { title: 'Daily Service Reports' },
  'paper-prf': { title: 'Parts Request Forms' },
  'paper-forms': { title: 'All Form Groups' },
  'tools': { title: 'Tool Inventory' },
  'timesheet': { title: 'Timesheet' },
  'expenses': { title: 'All Expenses' },
  'pto': { title: 'Sick / Vacation' },
  'howto': { title: 'How To' },
  'support': { title: 'Support' },
  'about': { title: 'About' },
} as const satisfies { readonly [K in ScreenKey]: ScreenDef }

export const SCREEN_ROUTE: { readonly [K in ScreenKey]: keyof typeof ROUTE } = {
  'schedule': 'schedule', 'wor': 'worList', 'paper-dsr': 'paperDsr', 'paper-prf': 'paperPrf', 'paper-forms': 'paperForms',
  'tools': 'tools', 'timesheet': 'timesheet', 'expenses': 'expenses', 'pto': 'pto', 'howto': 'howto', 'support': 'support', 'about': 'about',
}

export interface MenuItem { readonly screen: ScreenKey; readonly label: string; readonly code?: string }
export interface MenuGroup { readonly title: string; readonly items: readonly MenuItem[] }

/** Drawer groups, in order. No badges, on purpose. */
export const NAV_MENU: readonly MenuGroup[] = [
  { title: 'Jobs', items: [{ screen: 'schedule', label: 'Schedule' }, { screen: 'wor', label: 'Work Order Request', code: 'WOR' }] },
  {
    title: 'Completed Paperwork', items: [
      { screen: 'paper-dsr', label: 'Daily Service Report', code: 'DSR' },
      { screen: 'paper-prf', label: 'Parts Request Form', code: 'PRF' },
      { screen: 'paper-forms', label: 'Forms' }],
  },
  { title: 'Inventory', items: [{ screen: 'tools', label: 'Tool Inventory' }] },
  { title: 'Time & Pay', items: [{ screen: 'timesheet', label: 'Timesheet' }, { screen: 'expenses', label: 'Expenses' }, { screen: 'pto', label: 'Sick / Vacation' }] },
  { title: 'Help', items: [{ screen: 'howto', label: 'How To' }, { screen: 'support', label: 'Support' }, { screen: 'about', label: 'About' }] },
]

/** [what, how], verbatim from the shell spec §4. The Schedule renders its tip inside its dark band ('box'). */
export const TIPS: { readonly [K in TipKey]: readonly [what: string, how: string] } = {
  'schedule': ['Your jobs for the week.', 'Pick a day. Complete your JSA before completing the DSR. All other forms can be completed as needed. A red header means it requires your attention.'],
  'wor': ['Ask dispatch to set up a new job.', 'Tap + New Work Order Request, or Duplicate a past one to save time.'],
  'paper-dsr': ['Every Daily Service Report you’ve started or turned in.', 'Edit ones that are Drafts or Rejected, then resubmit. Submitted, Approved and Do Not Resubmit reports are read only.'],
  'paper-prf': ['Parts and materials you’ve asked for.', 'Edit ones that are Drafts or Rejected, then resubmit. Submitted, Approved and Do Not Resubmit requests are read only.'],
  'paper-forms': ['Inspection and test forms, grouped by job and form type.', 'Open a group, fill in serial #, test pressure and result, then submit.'],
  'tools': ['Every tool you’re responsible for, plus your counts, sign-offs and reports.', 'Start on My Tools. A red number on a tab means something is waiting on you.'],
  'job-jsa': ['Safety analyses for this job.', 'A JSA must be done before work starts each day.'],
  'timesheet': ['Your hours for the week.', 'Time comes into the timesheet once your DSR is approved. Check each day, then approve the week when complete.'],
  'expenses': ['Costs you need paid back.', 'Add each expense with its area, amount and receipt. Your report stays open so you can keep adding to it until you’re ready to submit.'],
  'pto': ['Your remaining sick and vacation hours.', 'Each card shows hours left, with how they were given and used below.'],
  'howto': ['Step-by-step guides for everything in RotoTrack.', 'Pick a topic and follow the steps. Tap Open to go straight to that screen.'],
  'support': ['Send an issue to IT support.', 'Tap New Ticket, describe the problem and send it. Track its status here.'],
  'about': ['App version and company info.', 'Check your version here if support asks.'],
}

/** Route-level chrome. A menu screen is a stack root whose Back goes to Schedule (Schedule itself has no Back).
 *  A detail route names the screen Back goes to on a cold deep link; `up` is required so it can't be forgotten. */
export type NavMeta =
  | { readonly kind: 'screen'; readonly screen: ScreenKey; readonly hideNewWor?: true }
  | {
      readonly kind: 'detail'
      readonly title: string | ((s: ActivatedRouteSnapshot) => string)
      readonly up: ScreenKey
      /** Where a cold deep link's Back lands when the `up` screen alone is not specific enough (a tab of it). */
      readonly upPath?: string
      readonly tip?: TipKey | ((s: ActivatedRouteSnapshot) => TipKey | null)
      readonly hideNewWor?: true
    }

export type DetailMeta = Extract<NavMeta, { kind: 'detail' }>

/** Route `data` helpers: the only way shell.routes.ts writes NavMeta. */
export function screenRoute(screen: ScreenKey, extra?: { hideNewWor?: true }): { nav: NavMeta } {
  return { nav: { kind: 'screen', screen, ...extra } }
}

export function detailRoute(meta: Omit<DetailMeta, 'kind'>): { nav: NavMeta } {
  return { nav: { kind: 'detail', ...meta } }
}

/** The title a route contributes to the Back label of the next level. */
export function titleFor(meta: NavMeta, s: ActivatedRouteSnapshot): string {
  if (meta.kind === 'screen') return SCREENS[meta.screen].title
  return typeof meta.title === 'function' ? meta.title(s) : meta.title
}

/** Tip strip for a route: a screen's own tip (Schedule excluded: it renders its tip inline), or the detail route's tip. */
export function tipFor(meta: NavMeta, s: ActivatedRouteSnapshot): TipKey | null {
  if (meta.kind === 'screen') return meta.screen === 'schedule' ? null : meta.screen
  if (!meta.tip) return null
  return typeof meta.tip === 'function' ? meta.tip(s) : meta.tip
}
